import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ArrowCounterClockwise, ClipboardText } from "@phosphor-icons/react";

const STORAGE_KEY = "vs_departure_checked";

function loadChecked() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

export default function GuestChecklist({ items }) {
  const [checked, setChecked] = useState(loadChecked);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
  }, [checked]);

  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));

  const grouped = useMemo(() => {
    const g = {};
    (items || []).forEach((it) => {
      (g[it.category] = g[it.category] || []).push(it);
    });
    return g;
  }, [items]);

  if (!items || items.length === 0) return null;

  const total = items.length;
  const done = items.filter((it) => checked[it.id]).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  // Embedded inside the Check-Out guide section (renters see the simple list instead)
  return (
    <div className="mt-12" data-testid="departure-checklist">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="inline-flex items-center gap-2 text-accent">
          <ClipboardText size={18} weight="duotone" />
          <span className="subhead text-accent text-sm">Departure Checklist</span>
        </div>

        <div className="mt-5 mb-8 border border-border px-5 py-4 bg-secondary/40 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="departure-progress">
            <span className="cms-label text-foreground/60">Progress</span>
            <div className="flex-1 min-w-36">
              <Progress value={pct} className="h-2" />
            </div>
            <span className="font-display text-xl text-foreground">
              {done}<span className="text-muted-foreground text-base"> / {total}</span>
            </span>
            <span className="text-sm text-muted-foreground">
              {pct === 100 ? "All set — safe travels!" : `${pct}%`}
            </span>
            <button
              onClick={() => setChecked({})}
              data-testid="departure-reset-btn"
              className="inline-flex items-center gap-2 text-sm text-accent hover:opacity-70 transition-opacity ml-auto"
            >
              <ArrowCounterClockwise size={15} /> Start over
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-x-10 gap-y-6" data-testid="departure-groups">
            {Object.entries(grouped).map(([cat, list]) => (
              <div key={cat} data-testid={`departure-group-${cat}`}>
                <p className="cms-label text-accent mb-3">{cat}</p>
                <div className="space-y-px bg-border border border-border">
                  {list.map((it) => {
                    const isDone = !!checked[it.id];
                    return (
                      <label
                        key={it.id}
                        data-testid={`departure-item-${it.id}`}
                        className="flex items-start gap-3 bg-background px-4 py-2.5 cursor-pointer"
                      >
                        <Checkbox
                          checked={isDone}
                          onCheckedChange={() => toggle(it.id)}
                          data-testid={`departure-checkbox-${it.id}`}
                          className="mt-0.5 data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                        />
                        <span
                          className={
                            "text-sm leading-snug transition-colors " +
                            (isDone ? "line-through text-muted-foreground" : "text-foreground/90")
                          }
                        >
                          {it.text}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
      </motion.div>
    </div>
  );
}
